import { OutlinedInput, Button, Typography, CircularProgress, Chip, Switch } from "@mui/material";
import React, { useEffect, useState } from "react";
import ListPageHeader, { ListPageHeaderLeft, ListPageHeaderRight } from "../../../../Components/Misc/ListPageHeader";

import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import AddIcon from "@mui/icons-material/Add";
import WarningIcon from "@mui/icons-material/Warning";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { ModalContext } from "../../../../Components/Modals";
import { ingestService } from "../../../../Core/Api/services";
import GlobalConst from "../../../../Core/Utils/Constants/globals";
import DataTable, { CellSize } from "../../../../Components/Table/table";
import { GridActionsCellItem, GridColDef, GridRowParams } from "@mui/x-data-grid-premium";
import { IngestConfigPackColumnSet } from "../../../../Core/Api/Types/ingest-config-pack-column-set";
import useCustomSnackbar from "../../../../Utils/Hooks/useCustomSnackbar";
import { UserHasAcl } from "../../../../Utils/helpers";
import { AclTypes } from "../../../../Core/Api/Types/acl-types";
import ModalCreateColumn, {
    ModalCreateColumnElementProps,
} from "../../../../Components/Modals/ModalCreateColumn/modal-create-column";
import ModalEditColumn, {
    ModalEditColumnElementProps,
} from "../../../../Components/Modals/ModalEditColumn/modal-edit-column";
import { useTranslation } from "react-i18next";
import TableUuids from "../../../../Components/Table/table-uuids";

enum FieldHandleChange {
    IsEditable,
    IsHidden,
    IsMandatory,
    IsToFillDuringIngest,
    Label,
    Order,
}

function IngestConfigColset() {
    const { t } = useTranslation();
    const modalContext = React.useContext(ModalContext);
    const { openModal } = { ...modalContext };
    const enqueueCustomSnackbar = useCustomSnackbar();

    const [cols, setCols] = useState<IngestConfigPackColumnSet[]>([]);
    const [filterText, setFilterText] = useState("");
    const [iteration, setIteration] = useState(0);
    const [reload, setReload] = useState(0);
    const [isLoading, setLoading] = useState(true);

    const aclUpdate = UserHasAcl(AclTypes.aclIngestConfigColumnSetUpdate);

    const handleValueChange = (event: any, value: any, fieldId: number, type: FieldHandleChange) => {
        let newValue = cols;

        let match = newValue.find((x) => x.columnFieldInfos?.id === fieldId);

        if (!match) return;

        switch (type) {
            case FieldHandleChange.IsEditable:
                match.columnInfos!.isEditable = value;
                break;
            case FieldHandleChange.IsHidden:
                match.columnInfos!.isHidden = value;
                break;
            case FieldHandleChange.IsMandatory:
                match.columnInfos!.isMandatory = value;
                break;
            case FieldHandleChange.IsToFillDuringIngest:
                match.columnInfos!.isToFillDuringIngest = value;
                break;
            case FieldHandleChange.Label:
                match.columnInfos!.label = value;
                break;
            case FieldHandleChange.Order:
                match.columnInfos!.order = value;
                break;
        }

        // Flag as modified
        match.columnInfos!.isNew = true;

        setCols(newValue);
        setIteration((prev) => ++prev);
    };

    const handleUpdate = (fieldId: number, reload: boolean = false) => {
        let newValue = cols;

        let match = newValue.find((x) => x.columnFieldInfos?.id === fieldId);

        if (!match) return;

        // Flag as modified
        match.columnInfos!.isNew = false;

        ingestService.UpdateIngestConfigColumnSet(match.columnInfos!).then((response) => {
            setCols(newValue);
            setIteration((prev) => --prev);
            enqueueCustomSnackbar(t("alert:success.columnSaved"), "success");

            if (reload) setReload((prev) => ++prev);
        });
    };

    const columnsAction: GridColDef[] = [
        {
            field: "order",
            headerName: t("common:order"),
            minWidth: CellSize.xxsmall,
            flex: CellSize.xxsmall,
            valueGetter: (value: any, row: any) => row?.columnInfos.order,
            renderCell: (data: any) => (
                <Typography color="text.secondary" fontWeight={500}>
                    {data.value}
                </Typography>
            ),
        },
        {
            field: "id",
            headerName: "Id",
            minWidth: CellSize.small,
            flex: CellSize.small,
            valueGetter: (value: any, row: any) => row?.columnFieldInfos.id,
            renderCell: (data: any) => (
                <Typography color="primary.text" fontWeight={500}>
                    {data.value}
                </Typography>
            ),
        },
        {
            field: "label",
            headerName: t("common:labelUpperCase"),
            minWidth: CellSize.large,
            flex: CellSize.large,
            valueGetter: (value: any, row: any) => row?.columnInfos.label,
            renderCell: (data: any) => (
                <OutlinedInput
                    placeholder={t("common:label")}
                    id="external-reference"
                    defaultValue={data.value}
                    onChange={(e) =>
                        handleValueChange(e, e.currentTarget.value, data?.row?.columnFieldInfos.id, FieldHandleChange.Label)
                    }
                />
            ),
        },
        {
            field: "isEditable",
            headerName: t("table:headerName.editable"),
            type: "boolean",
            minWidth: CellSize.small,
            flex: CellSize.small,
            valueGetter: (value: any, row: any) => row?.columnInfos.isEditable,
            renderCell: (data: any) => (
                <Switch
                    size="small"
                    value={data.value}
                    checked={data.value}
                    onChange={(e, value) =>
                        handleValueChange(e, value, data?.row?.columnFieldInfos.id, FieldHandleChange.IsEditable)
                    }
                />
            ),
        },
        {
            field: "isHidden",
            headerName: t("table:headerName.hidden"),
            type: "boolean",
            minWidth: CellSize.small,
            flex: CellSize.small,
            valueGetter: (value: any, row: any) => row?.columnInfos.isHidden,
            renderCell: (data: any) => (
                <Switch
                    size="small"
                    value={data.value}
                    checked={data.value}
                    onChange={(e, value) =>
                        handleValueChange(e, value, data?.row?.columnFieldInfos.id, FieldHandleChange.IsHidden)
                    }
                />
            ),
        },
        {
            field: "isMandatory",
            headerName: t("common:mandatory"),
            type: "boolean",
            minWidth: CellSize.small,
            flex: CellSize.small,
            valueGetter: (value: any, row: any) => row?.columnInfos.isMandatory,
            renderCell: (data: any) => (
                <Switch
                    size="small"
                    value={data.value}
                    checked={data.value}
                    onChange={(e, value) =>
                        handleValueChange(e, value, data?.row?.columnFieldInfos.id, FieldHandleChange.IsMandatory)
                    }
                />
            ),
        },
        {
            field: "isToFillDuringIngest",
            headerName: t("table:headerName.fillDuringIngest"),
            type: "boolean",
            minWidth: CellSize.medium,
            flex: CellSize.medium,
            valueGetter: (value: any, row: any) => row?.columnInfos.isToFillDuringIngest,
            renderCell: (data: any) => (
                <Switch
                    size="small"
                    value={data.value}
                    checked={data.value}
                    onChange={(e, value) =>
                        handleValueChange(e, value, data?.row?.columnFieldInfos.id, FieldHandleChange.IsToFillDuringIngest)
                    }
                />
            ),
        },
        {
            field: "actions",
            headerName: "",
            type: "actions",
            minWidth: CellSize.actionsLarge,
            flex: CellSize.actionsLarge,
            align: "right",
            getActions: (params: GridRowParams) => [
                <GridActionsCellItem
                    icon={<ArrowUpwardIcon sx={{ color: "text.secondary" }} />}
                    label="Up"
                    onClick={(e) => {
                        handleValueChange(
                            e,
                            params?.row?.columnInfos.order - 1,
                            params?.row?.columnFieldInfos.id,
                            FieldHandleChange.Order
                        );
                        handleUpdate(params?.row?.columnFieldInfos.id!, true);
                    }}
                    disabled={params?.row?.columnInfos.order === 1 || !aclUpdate}
                />,
                <GridActionsCellItem
                    icon={<ArrowDownwardIcon sx={{ color: "text.secondary" }} />}
                    label={t("common:upperCaseDown")}
                    onClick={(e) => {
                        handleValueChange(
                            e,
                            params?.row?.columnInfos.order + 1,
                            params?.row?.columnFieldInfos.id,
                            FieldHandleChange.Order
                        );
                        handleUpdate(params?.row?.columnFieldInfos.id!, true);
                    }}
                    disabled={params?.row?.columnInfos.order === cols.length - 1 || !aclUpdate}
                />,
                <GridActionsCellItem
                    icon={<EditIcon sx={{ color: "primary.text" }} />}
                    label={t("common:editUpperCase")}
                    onClick={() => {
                        if (!openModal) return;
                        openModal<ModalEditColumnElementProps>(ModalEditColumn, { column: params.row });
                    }}
                    disabled={!aclUpdate}
                />,
                <GridActionsCellItem
                    icon={<SaveIcon sx={{ color: params?.row?.columnInfos.isNew ? "warning.main" : "text.primary" }} />}
                    label={t("button:saveChanges")}
                    onClick={() => {
                        handleUpdate(params?.row?.columnFieldInfos.id!);
                    }}
                    disabled={!aclUpdate}
                />,
            ],
        },
    ];

    useEffect(() => {
        ingestService.GetIngestMetadataFields().then((response) => {
            if (response.columns) setCols(response.columns!.filter((x) => x.columnInfos?.id! > 0));

            setLoading(false);
        });
    }, [reload]);
    return (
        <>
            <ListPageHeader sx={{ mb: 0 }}>
                <ListPageHeaderLeft>
                    <Typography variant="h5">
                        {isLoading ? (
                            GlobalConst.LOADING_TEXT
                        ) : (
                            <>
                                {cols?.length} {t("common:columns")}{" "}
                                {iteration > 0 && cols?.filter((x) => x.columnInfos?.isNew).length > 0 && (
                                    <Chip
                                        sx={{ marginLeft: 1.5 }}
                                        color="warning"
                                        icon={<WarningIcon />}
                                        label={t("chip:label.modificationNotSaved", {
                                            count: cols?.filter((x) => x.columnInfos?.isNew).length,
                                        })}
                                    />
                                )}
                            </>
                        )}
                    </Typography>
                </ListPageHeaderLeft>
                <ListPageHeaderRight>
                    {UserHasAcl(AclTypes.aclIngestConfigColumnSetCreate) && (
                        <Button
                            startIcon={<AddIcon />}
                            color="primary"
                            variant="contained"
                            id="tutorial-ingest-create-column"
                            onClick={() => {
                                if (!openModal) return;
                                openModal<ModalCreateColumnElementProps>(ModalCreateColumn, {});
                            }}
                            disabled={isLoading}
                        >
                            {t("button:createColumn")}
                        </Button>
                    )}
                </ListPageHeaderRight>
            </ListPageHeader>

            <DataTable
                loading={isLoading}
                tableKey={TableUuids.INGEST_CONFIG_COLSET_TABLE}
                rows={cols}
                columns={columnsAction}
                initialState={{
                    pinnedColumns: { left: ["order", "id", "label"], right: ["actions"] },
                }}
                getRowId={(row: IngestConfigPackColumnSet) => row.columnFieldInfos?.id!}
            />
        </>
    );
}

export default IngestConfigColset;
